import { evaluate } from 'mathjs';
// Client-side formula calculator that mimics backend FormulaEngine
class FormulaCalculator {
  constructor() {
    this.variables = {};
    this.functions = {
      ROUND: (value, decimals = 2) => Number(Math.round(value + 'e' + decimals) + 'e-' + decimals),
      MAX: (...values) => Math.max(...values.filter(v => !isNaN(v))),
      MIN: (...values) => Math.min(...values.filter(v => !isNaN(v))),
      IF: (condition, trueValue, falseValue) => condition ? trueValue : falseValue,
      ABS: (value) => Math.abs(value)
    };
  }

  // Set variables for formula evaluation
  setVariables(variables) {
    this.variables = { ...variables };
  }

  // Get variable value
  getVariableValue(varName) {
    if (varName.startsWith('table.')) {
      const field = varName.replace('table.', '');
      const value = this.variables[field] || 0;
      return value;
    }
    if (varName.startsWith('template.usage')) {
      const parts = varName.split('.');
      if (parts.length >= 3) {
        const usageKey = parts[1]; // usage_1, usage_2, etc.
        const field = parts[2]; // ratio, rowResult, etc.
        // Convert usage_1 to usage1 for variable lookup
        const normalizedKey = usageKey.replace('usage_', 'usage');
        const lookupKey = `${normalizedKey}_${field}`;
        const value = this.variables[lookupKey] || 0;
        return value;
      }
    }
    const value = this.variables[varName] || 0;
    return value;
  }

  // Parse and evaluate formula
  evaluateFormula(formula) {
    try {
      // Replace variables with their values
      let processedFormula = formula;
      
      // Replace table.* variables
      processedFormula = processedFormula.replace(/table\.(\w+)/g, (match, field) => {
        const value = this.getVariableValue(match);
        return `(${value})`;
      });

      // Replace template.usage* variables
      processedFormula = processedFormula.replace(/template\.usage_(\d+)\.(\w+)/g, (match, usageNum, field) => {
        const value = this.getVariableValue(`usage${usageNum}_${field}`);
        return `(${value})`;
      });

      // Replace function calls
      Object.keys(this.functions).forEach(funcName => {
        const regex = new RegExp(`${funcName}\\(([^)]+)\\)`, 'g');
        processedFormula = processedFormula.replace(regex, (match, args) => {
          const argValues = args.split(',').map(arg => {
            const trimmed = arg.trim();
            if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
              return trimmed.slice(1, -1);
            }
            return trimmed;
          });
          return `this.functions.${funcName}(${argValues.join(',')})`;
        });
      });

      // Evaluate the processed formula
      const result = evaluate(processedFormula);
      
      // Check for invalid results
      if (isNaN(result) || !isFinite(result)) {
        console.warn(`Invalid formula result: ${result}`);
        return 0;
      }

      return Number(result);
    } catch (error) {
      console.error(`Formula evaluation error: ${error.message}`, { formula, variables: this.variables });
      return 0; // Return 0 on error instead of throwing
    }
  }

  // Execute multiple formulas in order
  executeFormulas(formulas) {
    const results = {};
    
    // Sort formulas by execution order
    const sortedFormulas = formulas.sort((a, b) => a.execution_order - b.execution_order);
    
    for (const formula of sortedFormulas) {
      try {
        const result = this.evaluateFormula(formula.formula_expression);
        results[formula.target_field] = result;
        
        // Update variables for next formulas
        if (formula.target_field.startsWith('table.')) {
          const field = formula.target_field.replace('table.', '');
          this.variables[field] = result;
        } else if (formula.target_field.startsWith('template.usage')) {
          const parts = formula.target_field.split('.');
          if (parts.length >= 3) {
            const usageKey = parts[1];
            const field = parts[2];
            // Convert usage_1 to usage1 for variable storage
            const normalizedKey = usageKey.replace('usage_', 'usage');
            const varKey = `${normalizedKey}_${field}`;
            this.variables[varKey] = result;
          }
        }
      } catch (error) {
        console.error(`Failed to execute formula: ${formula.target_field}`, error);
        results[formula.target_field] = 0; // Default to 0 on error
      }
    }
    
    return results;
  }

  // Create default formulas for compatibility
  static getDefaultFormulas() {
    return []; // No hardcoded formulas - use default template (ID: 1) formulas instead
  }
}

export default FormulaCalculator;
